import { useState, useEffect, useCallback, useRef } from 'react';

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
}

interface RateLimitState {
  attempts: number;
  lastAttempt: number;
  isBlocked: boolean;
  remainingTime: number;
}

export const useRateLimit = (config: RateLimitConfig) => {
  const [state, setState] = useState<RateLimitState>({
    attempts: 0,
    lastAttempt: 0,
    isBlocked: false,
    remainingTime: 0,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  const checkRateLimit = useCallback((): boolean => {
    const now = Date.now();
    const currentState = stateRef.current;
    const timeSinceLastAttempt = now - currentState.lastAttempt;

    // Reset attempts if window has passed
    if (timeSinceLastAttempt > config.windowMs) {
      setState(prev => ({
        ...prev,
        attempts: 0,
        isBlocked: false,
        remainingTime: 0,
      }));
      return true;
    }

    // Check if user is blocked
    if (currentState.attempts >= config.maxAttempts) {
      const remainingTime = config.windowMs - timeSinceLastAttempt;
      setState(prev => ({
        ...prev,
        isBlocked: true,
        remainingTime,
      }));
      return false;
    }

    return true;
  }, [config]);

  const recordAttempt = useCallback(() => {
    const now = Date.now();
    setState(prev => ({
      ...prev,
      attempts: prev.attempts + 1,
      lastAttempt: now,
    }));
  }, []);

  const resetRateLimit = useCallback(() => {
    setState({
      attempts: 0,
      lastAttempt: 0,
      isBlocked: false,
      remainingTime: 0,
    });
  }, []);

  // Auto-reset when window expires
  useEffect(() => {
    if (state.isBlocked && state.remainingTime > 0) {
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          isBlocked: false,
          remainingTime: 0,
        }));
      }, state.remainingTime);

      return () => clearTimeout(timer);
    }
  }, [state.isBlocked, state.remainingTime]);

  return {
    checkRateLimit,
    recordAttempt,
    resetRateLimit,
    isBlocked: state.isBlocked,
    remainingTime: state.remainingTime,
    attempts: state.attempts,
    maxAttempts: config.maxAttempts,
  };
};
